import React, { useEffect, useMemo, useCallback, useState } from 'react';
import {
  CompassCardComponent,
  CompassCardComponentProps,
} from 'react-scale/corp/views/DataCompassV2/types';
import { useCompassCard } from '@scale/compass-ui/store';
import type { CommunicationsCenterSendContext } from '@scale/genai-compass-shared/cards/communications_center_send';
import { CardType } from '@scale/genai-compass-shared/cardTypes';
import { PodGroup, EmailTemplateData } from '@scale/shared-types';
import {
  Text,
  TextField,
  Select,
  Icon,
  Tooltip,
  Callout,
  Flex,
  Box,
  FormField,
  Button,
  Popover,
  Checkbox,
  ScrollArea,
} from '@scale/scaleui-radix';
import {
  faInfoCircle,
  faChevronDown,
  faCheck,
  faExclamationTriangle,
} from '@fortawesome/pro-solid-svg-icons';
import { useQuery } from 'react-query';
import { getEmailCommsTemplateVersions } from 'react-scale/corp/lib/api/growth';

const CommunicationsCenterCardComponent: CompassCardComponent = ({
  index,
}: CompassCardComponentProps) => {
  const { card, updateCardContext, updateCardCanSubmit } = useCompassCard(index);
  const { retrievedOutput } = useCompassCard(index - 1);
  const columns = useMemo(
    () => retrievedOutput?.response?.schema || [],
    [retrievedOutput?.response?.schema],
  );

  // State for multi-select popover
  const [isMultiSelectOpen, setIsMultiSelectOpen] = useState(false);

  // Properly cast the context with type safety
  const context = useMemo(() => {
    const defaultContext: CommunicationsCenterSendContext = {
      cardType: CardType.COMMUNICATIONS_CENTER_SEND,
      templateId: '',
      communicationName: '',
      templateVariableColumnIds: [],
      podGroup: PodGroup.None,
    };

    if (card?.defaultContext?.cardType === CardType.COMMUNICATIONS_CENTER_SEND) {
      return card.defaultContext as CommunicationsCenterSendContext;
    }

    return defaultContext;
  }, [card?.defaultContext]);

  // Fetch template details when templateId changes
  const { data: templateData, isLoading: isLoadingTemplate } = useQuery(
    ['emailTemplate', context.templateId],
    async () => {
      if (!context.templateId?.trim()) return null;
      const versions = await getEmailCommsTemplateVersions(context.templateId);
      return versions?.[0] as EmailTemplateData | null; // First one is the latest
    },
    {
      enabled: !!context.templateId?.trim(),
      retry: false,
    },
  );

  // Calculate missing template variables
  const missingTemplateVariables = useMemo(() => {
    if (!templateData?.variables) return [];

    const selectedColumns = columns.filter(col =>
      context.templateVariableColumnIds?.includes(col.id || col.header),
    );

    // Get all available column names (including email which is always available)
    const availableVariables = new Set<string>(['email']);

    selectedColumns.forEach(col => {
      if (col.header) availableVariables.add(col.header);
      if (col.id) availableVariables.add(col.id);
    });

    // Find missing variables
    return templateData.variables.filter(variable => !availableVariables.has(variable));
  }, [templateData?.variables, columns, context.templateVariableColumnIds]);

  // Generic context update helper
  const updateContext = useCallback(
    (updates: Partial<CommunicationsCenterSendContext>) => {
      const updatedContext: CommunicationsCenterSendContext = {
        ...context,
        ...updates,
      };
      updateCardContext(updatedContext);
    },
    [context, updateCardContext],
  );

  // Handle template variable column selection
  const setSelectedTemplateVariableColumnsByIds = useCallback(
    (ids: string[]) => {
      updateContext({ templateVariableColumnIds: ids });
    },
    [updateContext],
  );

  // Handle individual column toggle
  const toggleColumn = useCallback(
    (columnId: string) => {
      const currentIds = context.templateVariableColumnIds || [];
      const newIds = currentIds.includes(columnId)
        ? currentIds.filter(id => id !== columnId)
        : [...currentIds, columnId];
      setSelectedTemplateVariableColumnsByIds(newIds);
    },
    [context.templateVariableColumnIds, setSelectedTemplateVariableColumnsByIds],
  );

  // Get display text for selected columns
  const selectedColumnsText = useMemo(() => {
    const selectedIds = context.templateVariableColumnIds || [];
    if (selectedIds.length === 0) {
      return 'Select columns for template variables...';
    }
    if (selectedIds.length === 1) {
      const column = columns.find(col => (col.id || col.header) === selectedIds[0]);
      return column?.header || column?.id || selectedIds[0];
    }
    return `${selectedIds.length} columns selected`;
  }, [context.templateVariableColumnIds, columns]);

  // Update card can submit status
  useEffect(() => {
    const canSubmit =
      !!context.templateId?.trim() &&
      !!context.communicationName?.trim() &&
      missingTemplateVariables.length === 0;
    updateCardCanSubmit(canSubmit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.templateId, context.communicationName, missingTemplateVariables.length]);

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* Instructions */}
      <Callout.Root color="blue">
        <Callout.Text>
          <Text as="p" className="text-sm">
            This card sends emails using Growth Center's infrastructure. Select an email template
            and provide a communication name. The card will automatically detect email addresses
            from the previous card's data and send personalized emails to each recipient.
          </Text>
        </Callout.Text>
      </Callout.Root>

      {/* Template ID Field */}
      <Box maxWidth="400px">
        <FormField.Root>
          <FormField.Label htmlFor="template-id">
            <Flex align="center" gap="2">
              Template ID
              <Tooltip content="The ID of the email template to use for sending communications.">
                <Icon icon={faInfoCircle} className="text-gray-500 cursor-help" label="Info" />
              </Tooltip>
            </Flex>
          </FormField.Label>
          <TextField.Root
            id="template-id"
            placeholder="Enter template ID"
            value={context.templateId}
            onChange={e => updateContext({ templateId: e.target.value })}
            color={context.templateId === '' ? 'red' : undefined}
          />
          {context.templateId === '' && (
            <Text size="2" color="red">
              Template ID is required
            </Text>
          )}
          {context.templateId && !isLoadingTemplate && !templateData && (
            <Text size="2" color="red">
              Template not found
            </Text>
          )}
        </FormField.Root>
      </Box>

      {/* Communication Name Field */}
      <Box maxWidth="400px">
        <FormField.Root>
          <FormField.Label htmlFor="communication-name">Communication Name</FormField.Label>
          <TextField.Root
            id="communication-name"
            placeholder="Enter communication name"
            value={context.communicationName}
            onChange={e => updateContext({ communicationName: e.target.value })}
            color={context.communicationName === '' ? 'red' : undefined}
          />
          {context.communicationName === '' && (
            <Text size="2" color="red">
              Communication name is required
            </Text>
          )}
        </FormField.Root>
      </Box>

      {/* Pod Group Field */}
      <Box maxWidth="400px">
        <FormField.Root>
          <FormField.Label htmlFor="pod-group">
            <Flex align="center" gap="2">
              Pod Group
              <Tooltip content="Pod information is used to determine the unsubscribe link in the email footer.">
                <Icon icon={faInfoCircle} className="text-gray-500 cursor-help" label="Info" />
              </Tooltip>
            </Flex>
          </FormField.Label>
          <Select.Root
            value={context.podGroup || PodGroup.None}
            onValueChange={value => updateContext({ podGroup: value as PodGroup })}
          >
            <Select.Trigger id="pod-group" placeholder="Select pod group..." />
            <Select.Content>
              {Object.values(PodGroup).map(podGroup => (
                <Select.Item key={podGroup} value={podGroup}>
                  {podGroup}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </FormField.Root>
      </Box>

      {/* Template Variable Columns */}
      <Box maxWidth="400px">
        <FormField.Root>
          <FormField.Label>
            <Flex align="center" gap="2">
              Template Variable Columns
              <Tooltip content="Select columns to use as template variables for email personalization.">
                <Icon icon={faInfoCircle} className="text-gray-500 cursor-help" label="Info" />
              </Tooltip>
            </Flex>
          </FormField.Label>

          {/* Show template variables info if available */}
          {templateData?.variables && templateData.variables.length > 0 && (
            <Box className="mb-2">
              <Text size="2" color="gray">
                Required variables: {templateData.variables.join(', ')}
              </Text>
              <br />
              <Text size="1" color="gray">
                Note: 'email' is always available from the email column
              </Text>
            </Box>
          )}

          <Popover.Root open={isMultiSelectOpen} onOpenChange={setIsMultiSelectOpen}>
            <Popover.Trigger>
              <Button
                variant="outline"
                className="w-full justify-between"
                onClick={() => setIsMultiSelectOpen(!isMultiSelectOpen)}
              >
                <Text size="2" className="truncate">
                  {selectedColumnsText}
                </Text>
                <Icon icon={faChevronDown} label="Expand" />
              </Button>
            </Popover.Trigger>
            <Popover.Content className="w-80" align="start">
              <ScrollArea className="h-64">
                <Box className="p-2">
                  {columns.length === 0 ? (
                    <Text size="2" color="gray">
                      No columns available from previous card
                    </Text>
                  ) : (
                    <Flex direction="column" gap="2">
                      {columns.map(column => {
                        const columnId = column.id || column.header;
                        const columnName = column.header || column.id;
                        const isSelected = (context.templateVariableColumnIds || []).includes(
                          columnId,
                        );

                        return (
                          <Flex
                            key={columnId}
                            align="center"
                            gap="2"
                            className="p-2 hover:bg-gray-50 rounded cursor-pointer"
                            onClick={() => toggleColumn(columnId)}
                          >
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleColumn(columnId)}
                            />
                            <Text size="2" className="flex-1">
                              {columnName}
                            </Text>
                            {isSelected && <Icon icon={faCheck} color="green" label="Selected" />}
                          </Flex>
                        );
                      })}
                    </Flex>
                  )}
                </Box>
              </ScrollArea>
            </Popover.Content>
          </Popover.Root>

          {/* Missing variables warning */}
          {missingTemplateVariables.length > 0 && (
            <Callout.Root color="red" className="mt-2">
              <Callout.Icon>
                <Icon icon={faExclamationTriangle} label="Warning" />
              </Callout.Icon>
              <Callout.Text>
                <Text size="2">
                  Missing required template variables: {missingTemplateVariables.join(', ')}
                </Text>
                <Text size="1" className="mt-1">
                  <br />
                  Please select columns that match these variable names.
                </Text>
              </Callout.Text>
            </Callout.Root>
          )}
        </FormField.Root>
      </Box>
    </div>
  );
};

export default CommunicationsCenterCardComponent;
